'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import MobileSidebar from '@eliadmin/core/src/components/header/mobile-sidebar';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { LuPlus } from 'react-icons/lu';
import { usePathname } from 'next/navigation';
import { HeaderLanguageMultistore } from '@eliadmin/core/src/components/header/header-language-multistore';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { isSlovakStore } from '@eliadmin/core/src/constants/store';
import { isServicesMenuItem } from './menu-utils';
import { LanguageSelect } from '@eliadmin/core/src/components/header/language-select';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  menu: Menu;
};

export function HeaderMain({ generalLayout, menu }: HeaderMainProps) {
  const pathname = usePathname();
  const menuFields = menu?.fields ?? [];

  const { t } = useTranslations();

  return (
    <MobileSearchProvider>
      <div className={cn('custom-section', 'h-[52px] bg-[#FAFAFA] sm:h-14 md:h-fit')}>
        <div className={cn('custom-container', 'flex items-center justify-between')}>
          <div className="flex shrink-0 items-center justify-start gap-3">
            <div className="flex lg:hidden">
              <MobileSidebar menu={menuFields} side="left" generalLayout={generalLayout} />
            </div>
            <Link prefetchOnHover prefetch={false} href={ROUTES.HOME()}>
              <Image
                src={generalLayout?.header?.logo?.url ?? '/images/placeholder.png'}
                alt={resolveImageAlt(generalLayout?.header?.logo, 'Stolárstvo Kaufmann')}
                width={0}
                height={0}
                className="h-6 w-auto sm:h-7 md:h-9 lg:h-9 xl:h-12"
                sizes="(min-width: 1280px) 210px, (min-width: 768px) 158px, (min-width: 640px) 123px, 105px"
                loading="eager"
              />
            </Link>
          </div>

          <div className="flex min-w-0 items-center justify-end gap-4 lg:gap-5 xl:gap-6">
            <ul className="hidden items-center justify-end gap-4 lg:flex lg:gap-4 xl:gap-6">
              {menuFields.map(item => {
                const isSlovak = isSlovakStore();
                const isActive = pathname === getSafeHref(item.path ?? '#');

                const hideItem = !isSlovak && item?.path === '#mesta-kde-posobime';

                if (!hideItem)
                  return (
                    <li
                      key={item.name}
                      className="relative flex !h-[120px] shrink-0 items-center justify-center"
                    >
                      <Link prefetchOnHover
                        prefetch={false}
                        href={getSafeHref(item.path ?? '#')}
                        className={cn(
                          'relative flex h-[120px] items-center justify-center whitespace-nowrap text-sm transition-all xl:text-base',
                          isActive
                            ? 'cursor-context-menu font-bold'
                            : 'font-normal hover:opacity-80',
                        )}
                      >
                        {item.name}
                        {isServicesMenuItem(item.path) && <sup className="text-primary">5</sup>}
                      </Link>
                      <div
                        className={cn(
                          'invisible absolute bottom-0 left-0 h-[3px] w-full bg-primary',
                          isActive && 'visible',
                        )}
                      />
                    </li>
                  );
              })}
            </ul>
            <Button
              asChild
              size={'custom'}
              className="h-[30px] shrink-0 gap-0.5 whitespace-nowrap px-1.5 sm:h-8 sm:gap-1 sm:px-3.5 md:h-10 md:gap-1.5 md:px-5 lg:h-11 lg:gap-2 lg:px-4 xl:h-12 xl:px-6"
            >
              <Link prefetchOnHover prefetch={false} href="/konfigurator">
                <LuPlus className="h-[18px] w-[18px] sm:h-5 sm:w-5 lg:h-5 lg:w-5 xl:h-6 xl:w-6" />
                {t('text818')}
              </Link>
            </Button>
            <HeaderLanguageMultistore />
          </div>
        </div>
      </div>
    </MobileSearchProvider>
  );
}
